import { controllerFactory } from "@utils/createController";
import { ManagedLocalStorage } from "@utils/managedLocalStorage";

import type * as IChunk from "./js";

import c from "./component.module.css";

interface SthenoEventObject extends Event {
  params: {
    mark?: string;
    ordered?: boolean;
  };
}

export default class extends controllerFactory<HTMLDivElement>()({
  targets: {
    attachmentInstance: null,
    attachmentTemplate: null,
    attachments: null,
    editor: null,
    editorRoot: null,
    emptyPreviewPlaceholder: null,
    filledPreviewPlaceholder: null,
    attachmentPreview: null,
    submit: HTMLButtonElement,
    toolbar: null,
    uploadInput: HTMLInputElement,
  },
  values: {
    attachmentsOpened: Boolean,
    cache: String,

    editorOpened: Boolean,
    fileServer: String,

    fileTypes: Array,
    inputName: String,
    internal: Boolean,
    lang: String,

    maxFileSize: Number,
    maxNumberOfFiles: Number,
    placeholder: String,

    uploadInputName: String,
  },
}) {
  private chunk: typeof IChunk;
  private setCache: (value?: string) => void;
  private uppy: IChunk.UppyWrapper;

  // The CodeMirror EditorView that will be used by the system
  private view: IChunk.EditorView;

  private addFormHandlers() {
    const form = this.element.closest("form");
    if (form) {
      form.addEventListener("turbo:submit-start", (e) => {
        this.submitStart(e as CustomEvent);
      });

      form.addEventListener("turbo:submit-end", () => {
        this.submitEnd();
      });
    }
  }

  private initCache() {
    const key = this.cacheValue;
    if (!key) return [null, () => {}] as const;

    // Only storing 10 drafts at the same time
    const manager = new ManagedLocalStorage(key, 10);

    return [
      manager.getItem<string>(),
      (value?: string) => {
        manager.setItem(value, 3);
      },
    ] as const;
  }

  private setDisabledOnSubmit(disabled: boolean) {
    this.submitTargets.forEach((btn) => {
      if (disabled && this.isConversationValid()) {
        btn.disabled = disabled;
      } else if (this.isConversationValid() && btn.disabled) {
        btn.disabled = false;
      }
    });
  }

  private setupUppy() {
    this.uppy = new this.chunk.UppyWrapper(this.fileServerValue, {
      allowedFileTypes: this.fileTypesValue as string[],
      maxFileSize: this.maxFileSizeValue * 1024 * 1024,
      maxNumberOfFiles: this.maxNumberOfFilesValue,
    });

    this.uppy
      .handleDnd(this.element, {
        onDragLeave: () => {
          this.element.classList.remove(c.drag);
        },
        onDragOver: () => {
          this.element.classList.add(c.drag);
        },
        onDrop: () => {
          this.element.classList.remove(c.drag);
          // Opening full view if it's not already
          this.togglePreview(false);
        },
      })
      .handleSuccess(this.attachmentsTarget, this.uploadInputNameValue)
      .handleThumnails((file, attachmentPreview) => {
        this.attachmentsOpenedValue = true;

        const template = this.attachmentTemplateTarget.cloneNode(
          true,
        ) as HTMLElement;
        template.dataset.sthenoTarget = "attachmentInstance";

        (template.querySelector("img") as HTMLImageElement).src =
          attachmentPreview;

        (template.querySelector("button") as HTMLButtonElement).onclick =
          () => {
            this.uppy.removeAttachment(
              this.attachmentsTarget,
              this.uploadInputNameValue,
              file.meta.fileId,
            );
            template.remove();

            if (!this.attachmentInstanceTargets.length) {
              // We've dropped last attachment
              this.attachmentsOpenedValue = false;
            }
          };

        this.attachmentsTarget.prepend(template);
      });
  }

  private togglePreview(showPreview?: Event | boolean) {
    if (typeof showPreview === "boolean") {
      this.editorOpenedValue = !showPreview;
    } else {
      this.editorOpenedValue = !this.editorOpenedValue;
    }
  }

  private updatePreviewText(text?: string) {
    // Replacing all extra white space (like newlines) with a single space to fit
    // more content into the attachmentPreview
    const currentText = (text ?? this.view.state.doc.toString()).replace(
      /\s+/g,
      " ",
    );
    this.filledPreviewPlaceholderTarget.innerText = currentText;
    if (currentText) {
      this.filledPreviewPlaceholderTarget.classList.remove("hidden");
      this.emptyPreviewPlaceholderTarget.classList.add("hidden");
    } else {
      this.filledPreviewPlaceholderTarget.classList.add("hidden");
      this.emptyPreviewPlaceholderTarget.classList.remove("hidden");
    }
  }

  attachmentsOpenedValueChanged() {
    if (this.attachmentsOpenedValue)
      this.attachmentsTarget.classList.remove("hidden");
    else this.attachmentsTarget.classList.add("hidden");
  }

  clearEditorState(): void {
    // Dispatch a transaction to clear the editor
    this.view.dispatch({
      changes: [
        {
          from: 0,
          insert: "",
          to: this.view.state.doc.length,
        },
      ],
    });
    this.attachmentInstanceTargets.forEach((el) => {
      el.remove();
    });
    this.attachmentsOpenedValue = false;
  }

  async connect() {
    this.element.classList.add(c.root);

    // Adding internal class if we're starting with internal editor
    if (this.internalValue) {
      this.element.classList.add(c.internal);
    }

    this.setDisabledOnSubmit(true);
    this.addFormHandlers();

    // Trimming to avoid some extra tab in the beginning of content
    const [cachedDraft, setCache] = this.initCache();
    const initialContent = cachedDraft ?? this.editorTarget.innerText.trim();

    this.setCache = setCache;

    // Dropping the content from DOM
    this.editorTarget.innerHTML = "";

    this.updatePreviewText(initialContent);
    this.setDisabledOnSubmit(!initialContent);

    this.chunk = await import("./js");

    this.setupUppy();

    this.view = new this.chunk.EditorView({
      parent: this.editorTarget,
      state: this.chunk.EditorState.create(
        this.chunk.getSthenoConfig({
          doc: initialContent,
          extensions: [
            this.chunk.placeholder(this.placeholderValue),
            this.chunk.tooltips({
              parent: this.editorTarget,
              position: "absolute",
            }),
            this.chunk.EditorView.updateListener.of(
              (update: IChunk.ViewUpdate) => {
                if (update.docChanged) {
                  setCache(update.state.doc.toString());
                  this.setDisabledOnSubmit(!update.state.doc.length);
                }
              },
            ),
            this.chunk.EditorView.domEventHandlers({
              dragenter(event) {
                event.preventDefault();
              },
              drop(event) {
                event.preventDefault();
              },
              keydown: (event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                  this.submitTargets?.[0]?.click();
                  event.preventDefault();
                }
              },
              paste: this.uppy.handlePaste.bind(this.uppy),
            }),
          ],
          lang: this.langValue,
        }),
      ),
    });

    this.editorOpenedValueChanged();
  }

  editorOpenedValueChanged() {
    if (!this.view) return;

    if (this.editorOpenedValue) {
      this.attachmentPreviewTarget.classList.add("hidden");
      this.editorRootTarget.classList.remove("hidden");

      this.view.focus();
    } else {
      this.attachmentPreviewTarget.classList.remove("hidden");
      this.editorRootTarget.classList.add("hidden");

      this.updatePreviewText();
    }
  }

  focusEditor(e: Event) {
    if (
      !this.editorOpenedValue &&
      // Preventing from stealing focus from other inputs
      !(e.target instanceof HTMLInputElement)
    ) {
      // Preventing an unnecessary `r` from appearing
      e.preventDefault();
      this.togglePreview(false);
    }
  }

  /**
   * If your Editor setup has a checkbox that's supposed to control theming of the editor,
   * use it to toggle to internal and back based on it.
   */
  internalCheckboxChange(event: Event) {
    if ((event.target as HTMLInputElement)?.checked) {
      this.internalValue = true;
      this.element.classList.add(c.internal);
    } else {
      this.internalValue = false;
      this.element.classList.remove(c.internal);
    }
  }

  isConversationValid(): boolean {
    return this.submitTarget.getAttribute("data-conversation-valid") != "false";
  }

  openUpload(): void {
    this.uploadInputTarget.click();
  }

  prependLine({ params }: SthenoEventObject) {
    if (!params.mark) return;

    if (params.ordered !== true) {
      this.chunk.prependLines(params.mark, this.view, params.ordered);
    } else {
      this.chunk.prependLines(params.mark, this.view);
    }
    this.view.focus();
  }

  submitEnd() {
    this.clearEditorState();
    this.setDisabledOnSubmit(false);

    // Dropping cache
    this.setCache?.();
  }

  submitStart(e: CustomEvent) {
    e.detail.formSubmission.fetchRequest.body.append(
      this.inputNameValue,
      this.view.state.doc.toString(),
    );

    // manually pass this along, since it's a disabled checkbox,
    // and won't be included in the form submission
    if (this.internalValue) {
      e.detail.formSubmission.fetchRequest.body.append(
        "message[internal]",
        "on",
      );
    }

    // This isn't very good from a11y point of view, but that's tolerable for not
    this.setDisabledOnSubmit(true);
  }

  uploadChange(e: Event): void {
    // Opening full view if it's not already
    this.togglePreview(false);
    this.uppy.handleInputChange(e);
  }

  wrap({ params }: SthenoEventObject) {
    if (!params.mark) return;
    this.chunk.wrapText(params.mark, this.view);
    this.view.focus();
  }
}
